/// <amd-module name="Feed/Legacy/Stores/FeedStore" />
import { IModuleStore } from "redux-dynamic-modules";

import { find, findIndex } from "OfficeFabric/Utilities";

import { announce, ProgressAnnouncer } from "VSS/Core/Util/Accessibility";
import * as Utils_String from "VSS/Core/Util/String";
import { IVssPageContext, IVssRestClientOptions } from "VSS/Platform/Context";
import * as Operations from "VSS/Platform/Contracts/Operations";
import { IVssScenario } from "VSS/Platform/Performance";
import { IReduxStoreService } from "VSS/Redux/StoreService";
import * as Store from "VSS/Reflux/Store";

import { HistoryBehavior } from "VSSUI/Navigation/ViewStateNavigation";
import { VssHubViewState } from "VSSUI/Navigation/VssHubViewState";
import { IPickListItem } from "VSSUI/PickList";
import { Filter, IFilterItemState, IFilterState } from "VSSUI/Utilities/Filter";
import { HubViewOptionKeys, HubViewState } from "VSSUI/Utilities/HubViewState";

import {
    CiConstants,
    FeedServiceInstanceId,
    HubActionStrings,
    HubViewDefaultPivots,
    PackageFilterBarConstants,
    PerfScenarios,
    SettingsConstants
} from "Feed/Common/Constants/Constants";
import { FeedView, Package, PackageMetrics, PackageVersion, UpstreamSource } from "Feed/Common/Contracts/Feed";
import { FeedRetrievalResult, FeedsResult, PackagesRetrievalResult } from "Feed/Common/Contracts/WebPage";
import { BatchDeprecateData, NpmBatchOperationType, NpmPackagesBatchRequest } from "Feed/Common/Protocols/Npm/Contracts/Npm";
import { NuGetBatchOperationType, NuGetPackagesBatchRequest } from "Feed/Common/Protocols/NuGet/Contracts/NuGet";
import * as PackageResources from "Feed/Common/Resources";
import { IFeedPerformanceService } from "Feed/Common/Services/FeedPerformanceService";
import { IError } from "Feed/Common/Types/IError";
import * as ReduxActions from "Feed/Core/LegacyActions/LegacyFeedActions";
import { FeedHubActions } from "Feed/Core/Redux/FeedHub/FeedHubActions";
import { NavigationActions, replaceHistoryPoint } from "Feed/Core/Redux/Navigation/NavigationActions";
import * as Actions from "Feed/Legacy/Actions/Actions";
import { FeedManager } from "Feed/Legacy/Actions/FeedList/FeedManager";
import { RestorePackages } from "Feed/Legacy/Actions/RecycleBin/RestorePackages";
import { RecycleBinActions } from "Feed/Legacy/Actions/RecycleBinActions";
import { SettingsActions } from "Feed/Legacy/Actions/SettingsActions";
import {
    INewFeedCreatedPayload,
    IPackageDeletedPayload,
    IPackageDeprecatedPayload,
    IPackageListedPayload,
    IPackageListedStatusChangedPayload,
    IPackagePayload,
    IPackagePromotedPayload,
    IPackageSelectionChangedPayload,
    IPromotePanelPayload
} from "Feed/Legacy/Common/ActionPayloads";
import { NavigationHandler } from "Feed/Legacy/Common/NavigationHandler";
import { PackageModifiedCache } from "Feed/Legacy/Common/PackageModifiedCache";
import {
    DependencyAction,
    IFeedSettingsState,
    IInternalUpstreamSettingsState,
    IRetentionPolicySettingsState
} from "Feed/Legacy/Components/Settings/IFeedSettingsState";
import { IFeedDataService } from "Feed/Legacy/DataServices/FeedsDataService";
import { FilterByEnabledProtocolDataService } from "Feed/Legacy/DataServices/FilterByEnabledProtocolDataService";
import { RecycleBinDataService } from "Feed/Legacy/DataServices/RecycleBinDataService";
import { SettingsDataService } from "Feed/Legacy/DataServices/SettingsDataService";
import { HubWebPageDataService } from "Feed/Legacy/DataServices/WebPageDataService";
import {
    buildAnnouncementListedStatusChangedForPackage,
    buildAnnouncementPackageFilterResults
} from "Feed/Legacy/Helpers/AccessibilityHelper";
import { CustomerIntelligenceHelper } from "Feed/Legacy/Helpers/CustomerIntelligenceHelper";
import { isFeedUnderMaintenance, isV2Feed } from "Feed/Legacy/Helpers/FeedCapabilityHelper";
import { getFullyQualifiedFeedId, getFullyQualifiedFeedName } from "Feed/Legacy/Helpers/FeedNameResolver";
import { PackageFilterBarHelper } from "Feed/Legacy/Helpers/PackageFilterBarHelper";
import { getPackageMetricsAsync } from "Feed/Legacy/Helpers/PackageMetricsHelper";
import * as PermissionHelper from "Feed/Legacy/Helpers/PermissionHelper";
import { getProjectIdForProtocol } from "Feed/Legacy/Helpers/ProjectHelper";
import { filterStateToUpstreamSource, getProtocolFromUpstreamSource } from "Feed/Legacy/Helpers/UpstreamHelper";
import { partition } from "Feed/Legacy/Protocols/Common/BatchHandling";
import { MinimalPackageDetails } from "Feed/Legacy/Protocols/Common/WebApi/PackagingShared.Contracts";
import { INpmDataService } from "Feed/Legacy/Protocols/Npm/NpmDataService";
import { IFeedNuGetDataService } from "Feed/Legacy/Protocols/NuGet/NuGetDataService";
import { IFeedProtocolProvider } from "Feed/Legacy/Protocols/ProtocolProvider";
import { HubAction, HubStateHelpers, IHubState } from "Feed/Legacy/Types/IHubState";
import { urlSourceToUpstreamSource } from "Feed/Legacy/Utils/Upstream";
import { Feed } from "Feed/Legacy/WebApi/VSS.CustomFeed.Contracts";
import * as OperationRestClientGetter from "TFS/Admin/Common/RestClient/Operations";
import { IGlobalMessagesService } from "TFS/GlobalBanner/GlobalMessagesService.Types";
import { FeedState } from "Types/FeedState";
import { RecycleBinState } from "Types/RecycleBinState";

export class FeedStore extends Store.Store {
    private pageContext: IVssPageContext;

    // ***Note***: There is some violation of the 'single-direction' flow in this store.
    // Specifically, the action handlers for operations invoked from the GeneralDialog
    // component need to invoke an action to close the dialog once they have finished
    // handling the Action.
    // We will be moving the processing to 'action-creators' and making the store's simpler.
    // At that point, this undesirable pattern will be removed. In the meantime, don't
    // perpetuate this pattern any further - see saponnu or philho if you feel you need to.

    constructor(pageContext: IVssPageContext, hubState: IHubState) {
        super("feed-FeedStore");
        this.pageContext = pageContext;

        const storeService = this.pageContext.getService<IReduxStoreService>("redux-store-service");
        this._reduxStore = storeService.getStore(null);

        this.initializeDataServices();
        this.initializeState();
        this.initializeRecyclebinState();
        this.hydrateFromDataIsland();
        this.hydrateFeedFromDataIsland(pageContext, hubState);

        this.feedManager = new FeedManager(this.pageContext, () => this.getFeedState()); // can I change this to getCurrentFeed?

        const showWelcomeMessage = this.checkWelcomeMessage();
        if (showWelcomeMessage === false) {
            const hubAction: HubAction = HubStateHelpers.getHubAction(hubState.action);
            this.hydratePackagesFromDataIsland(hubAction);
            this.displayErrorIfViewDoesntMatch(hubState);
            this.initializeFilters();
        }

        this.initializeAvailableProtocols();
        this._initializeActionListeners();
    }

    private initializeState(): void {
        this._currentState = {} as FeedState;
        this._currentState.selectedPackages = [];
        this._currentState.hubState = new HubViewState({
            defaultPivot: HubViewDefaultPivots.feed
        });
        this._currentState.isMultiPromotePanelOpen = false;
        this._currentState.multiCommandDropdownVersions = null;
        this._currentState.showCreateBadgePanel = false;
        this._filterText = null;
        this._packagesReFetched = null;
        this._currentState.packageIdMetricsMap = {};
    }

    private initializeRecyclebinState(): void {
        this._currentState.recycleBinState = {
            recycleBinPackages: [],
            isRecycleBinEnabled: false,
            recycleBinBatchOperationEnabled: false,
            packageNameFilterValue: null,
            dialogOpen: false,
            dialogProcessing: false,
            recycleBinJobErrorMessage: null,
            hubViewState: new VssHubViewState(this.pageContext, {
                pivotNavigationBehavior: HistoryBehavior.none, // we don't want to add the view to the url, that's why its HistoryBehaviour.none
                defaultPivot: HubViewDefaultPivots.recycleBin
            })
        } as RecycleBinState;
    }

    private initializeDataServices(): void {
        this._feedsDataService = this.pageContext.getService<IFeedDataService>("IFeedDataService");
        this._webPageDataService = this.pageContext.getService<HubWebPageDataService>("IFeedWebPageDataService");
        this._filterByEnabledProtocolDataService = this.pageContext.getService<FilterByEnabledProtocolDataService>(
            "IFeedFilterByEnabledProtocolDataService"
        );
        this._recycleBinDataService = this.pageContext.getService<RecycleBinDataService>("IFeedRecycleBinDataService");
    }

    private hydrateFromDataIsland(): void {
        this._currentState.feedUser = {
            identityDescriptors: this._webPageDataService.getCurrentUserDescriptor(),
            canAdministerFeeds: this._webPageDataService.userCanAdministerFeeds(),
            canCreateFeed: this._webPageDataService.userCanCreateFeed(),
            isAnonymousOrPublic: null /* Is set after selected feed is hydrated */
        };
        this._currentState.pageSize = this._webPageDataService.getPageSize();
        this._currentState.isNpmAllowUpstreamNameConflict = this._webPageDataService.getNpmUpstreamNameConflictFeatureFlag();
        this._currentState.isCustomPublicUpstreamsFeatureEnabled = this._webPageDataService.isCustomPublicUpstreamsFeatureEnabled();
        this._currentState.isProjectScopedUpstreamsFeatureEnabled = this._webPageDataService.isProjectScopedUpstreamsFeatureEnabled();
        this._currentState.metricsEnabled = this._webPageDataService.isPackageMetricsEnabled();
        this._currentState.upstreamSourceLimit = this._webPageDataService.getUpstreamSourceLimit();
        this._currentState.isGlobalPermMappedIdentifiersEnabled = this._webPageDataService.isGlobalPermMappedIdentifiersEnabled();
        this._currentState.isServiceEndpointUpstreamsFeatureEnabled = this._webPageDataService.isServiceEndpointUpstreamsFeatureEnabled();
    }

    private hydrateFeedFromDataIsland(pageContext:IVssPageContext, state: IHubState): void {
        const feed: string = state.feed ? state.feed.split("@")[0] : null;
        this._initializeFeedData(pageContext, feed, state);
    }

    private hydratePackagesFromDataIsland(action: HubAction): void {
        if (this._currentState.selectedFeed.deletedDate != null) {
            return;
        }
        if (action === HubAction.Feed || action === HubAction.ConnectToFeed) {
            const packageScenario = this.PerformanceService.startScenario(PerfScenarios.PackageListUpdated);

            this._updatePackageList(packageScenario, true);
        }

        if (action === HubAction.RecycleBin) {
            this._initializeRecycleBinPackageListData();
        }
    }

    private checkWelcomeMessage(): boolean {
        return this._currentState.displayWelcomeMessage === true || this._currentState.selectedFeed == null;
    }

    private displayErrorIfViewDoesntMatch(state: IHubState): void {
        if (
            this._currentState.selectedFeed == null ||
            state.feed == null ||
            this._currentState.selectedFeed.deletedDate != null
        ) {
            return;
        }

        // If feed@view does not exist or user does not have access to the feed/view then
        // the requested feed (state.feed) and the selected feed (this._currentState.selectedFeed) will be different
        // It won't work if users mixes GUIDs and human-friendly names, but it's such a corner case that it won't be solved here

        // Base properties would match if I request a base feed but only have access to a view
        const baseFeedPropertiesMatch: boolean =
            Utils_String.equals(state.feed, this._currentState.selectedFeed.name, true) ||
            Utils_String.equals(state.feed, this._currentState.selectedFeed.id, true);

        // Fully qualified properties would match if I request a view and that view was returned
        const fullyQualifiedPropertiesMatch: boolean =
            Utils_String.equals(state.feed, this._currentState.selectedFeed.fullyQualifiedName, true) ||
            Utils_String.equals(state.feed, this._currentState.selectedFeed.fullyQualifiedId, true);

        if (!baseFeedPropertiesMatch && !fullyQualifiedPropertiesMatch) {
            const message = Utils_String.format(PackageResources.Error_UrlFeedNotFound, state.feed);
            Actions.ErrorEncountered.invoke({ message } as IError);
        }
    }

    private getFilterState(): IFilterState {
        let newFilterState: IFilterState = {};
        const viewInSelectedFeedExists = this._currentState.selectedFeed && this._currentState.selectedFeed.view;
        if (viewInSelectedFeedExists) {
            const viewName = this._currentState.selectedFeed.view.name;
            newFilterState = this._updateSelectedViewFilter(newFilterState, viewName);
        }

        const isUpstreamEnabledEnabled: boolean =
            this._currentState.selectedFeed && this._currentState.selectedFeed.upstreamEnabled;
        if (isUpstreamEnabledEnabled) {
            const upstreamSource = this._currentState.selectedFeed.upstreamSource;
            newFilterState = PackageFilterBarHelper.setSourceToFilterState(
                this.pageContext,
                newFilterState,
                upstreamSource
            );
        }
        return newFilterState;
    }

    private initializeFilters(): void {
        const filterState = this.getFilterState();
        this._currentState.hubState.filter.setState(filterState, true /*suppressChangeEvent*/);
        this._currentState.hubState.viewOptions.setViewOption(HubViewOptionKeys.showFilterBar, true);
    }

    public async setHubState(action: string, state: IHubState): Promise<void> {
        switch (HubStateHelpers.getHubAction(action)) {
            case HubAction.Feed:
                // when feed is deleted
                if (state.feed == null) {
                    return;
                }

                try {
                    this._currentState.packagesLoading = true;
                    await this.feedManager.setFeedFromUrlAsync(state);
                    this._reduxStore.dispatch(FeedHubActions.feedSelected(this.feedManager.feed.fullyQualifiedId));
                } catch (error) {
                    Actions.ErrorEncountered.invoke({
                        message: error.message,
                        isCritical: true
                    } as IError);
                } finally {
                    this._currentState.packagesLoading = false;
                }
                this.emitChanged();
                break;
            case HubAction.RecycleBin:
            case HubAction.CreateFeed:
            case HubAction.Package:
            case HubAction.Settings:
            case HubAction.ConnectToFeed:
            case HubAction.RecycleBinPackage:
                break;
            default:
                Actions.ErrorEncountered.invoke({
                    message: Utils_String.format(PackageResources.Error_UrlActionNotSupported, action)
                } as IError);
                break;
        }
    }
    public getFeedManager(): FeedManager {
        return this.feedManager;
    }

    public getFeedState(): FeedState {
        return this._currentState;
    }

    public getCurrentFeed(): Feed {
        return this._currentState.selectedFeed;
    }

    public getCurrentFeedViews(): FeedView[] {
        return this._currentState.feedViews;
    }

    public getFeedSettingsState(): IFeedSettingsState {
        if (this._currentState.feedSettings == null) {
            this._initializeFeedSettingsState();
        }
        return this._currentState.feedSettings;
    }

    private _initializeFeedSettingsState(): void {
        const isUpstreamV2Enabled: boolean = isV2Feed(this._currentState.selectedFeed);
        const internalUpstreamSettings = {
            isV2Feed: isUpstreamV2Enabled,
            aadUpstreamsEnabled: this._webPageDataService.isOrgAadBacked(),
            isProjectScopedUpstreamsEnabled: this._webPageDataService.isProjectScopedUpstreamsFeatureEnabled()
        } as IInternalUpstreamSettingsState;

        const retentionPolicySettings = {
            retentionPolicy: null,
            retentionPolicyLoading: false,
            retentionPolicyToApply: null,
            retentionPolicyMinCountLimit: this._webPageDataService.retentionPolicyLimits().minimumCountLimit,
            retentionPolicyMaxCountLimit: this._webPageDataService.retentionPolicyLimits().maximumCountLimit,
            retentionPolicyMinDaysToKeepLimit: this._webPageDataService.retentionPolicyLimits().minimumDaysToKeepLimit,
            retentionPolicyMaxDaysToKeepLimit: this._webPageDataService.retentionPolicyLimits().maximumDaysToKeepLimit,
            retentionPolicyCountLimitDefault: this._webPageDataService.retentionPolicyLimits()
                .retentionPolicyCountLimitDefault,
            retentionPolicyDaysToKeepLimitDefault: this._webPageDataService.retentionPolicyLimits()
                .retentionPolicyDaysToKeepLimitDefault
        } as IRetentionPolicySettingsState;

        // expand state to include UI state, but use reference
        this._currentState.feedSettings = {
            // tslint:disable-next-line:no-any
            updateActionDependencies: (action: DependencyAction, data: any): void => {
                this.updateFeedSettingActionsDependencies(action, data);
            },
            feed: (): Feed => {
                return this._currentState.selectedFeed;
            },
            isUserAdmin: (): boolean => {
                if (this._currentState && this._currentState.selectedFeed) {
                    return PermissionHelper.isAdministrator(this._currentState.selectedFeed);
                } else {
                    return false;
                }
            },
            showDiscardDialog: false,
            showDeleteDialog: false,
            feedName: null,
            feedDescription: null,
            hideDeletedPackageVersions: null,
            badgesEnabled: null,
            isPermissionDataLoadedFromServer: false,
            feedPermissions: [],
            permissionsFilter: new Filter(),
            showAddUsersOrGroupsPanel: false,
            isViewsDataLoadedFromServer: false,
            views: [],
            viewPermissions: {},
            viewPermissionsToAdd: {},
            viewPermissionsToRemove: {},
            viewsFilter: new Filter(),
            showViewPanel: false,
            upstreamSourceFilter: new Filter(),
            displayAddUpstreamPanel: false,
            validationErrorBag: {},
            showWelcomeMessage: (): boolean => {
                return this._currentState.displayWelcomeMessage;
            },
            isSavingChanges: false,
            feeds: (): Feed[] => {
                return this._currentState.feeds;
            },
            internalUpstreamSettings,
            retentionPolicySettings,
            projectCollectionAdminGroupId: this._webPageDataService.getProjectCollectionAdminGroupId(),
            selectedPermissions: [],
            selectedViews: [],
            selectedUpstreamSources: [],
            upgradeAvailable: isUpstreamV2Enabled !== true,
            isCustomPublicUpstreamsFeatureEnabled: this._currentState.isCustomPublicUpstreamsFeatureEnabled,
            isProjectScopedUpstreamsFeatureEnabled: this._currentState.isProjectScopedUpstreamsFeatureEnabled,
            upgradeInProgress: isFeedUnderMaintenance(this._currentState.selectedFeed) === true,
            upstreamSourceLimit: this._currentState.upstreamSourceLimit,
            isServiceEndpointUpstreamsFeatureEnabled: this._currentState.isServiceEndpointUpstreamsFeatureEnabled
        } as IFeedSettingsState;

        this._currentState.feedSettings.hubViewState = new VssHubViewState(this.pageContext, {
            defaultPivot: HubViewDefaultPivots.settings
        });
    }

    // tslint:disable-next-line:no-any
    private updateFeedSettingActionsDependencies(action: DependencyAction, data: any): void {
        switch (action) {
            case DependencyAction.DeleteFeed:
                this._handleFeedDeletedAction(data as Feed);
                break;
            case DependencyAction.UpdateViews:
                this._handleFeedViewsUpdatedAction(data as FeedView[]);
                break;
            case DependencyAction.RestoreFeed:
                this._handleFeedRestoredAction(data as Feed);
                break;
            case DependencyAction.PermanentlyDeleteFeed:
                this._handleFeedPermanentlyDeletedAction(data as Feed);
                break;
        }
    }

    public feedStoreEmitChanged(): void {
        this.emitChanged();
    }

    public getFirstPackage(): Package {
        return this._currentState.packages[0];
    }

    private _initializeActionListeners(): void {
        Actions.FeedBreadcrumbSelected.addListener(this._handleFeedBreadCrumbSelectedAction);
        Actions.FeedListUpdated.addListener(this._handleFeedListUpdatedAction);
        Actions.FeedSelected.addListener(this._handleFeedSelectedAction);
        Actions.FeedUpdated.addListener(this._handleFeedUpdatedAction);
        Actions.FeedViewsUpdated.addListener(this._handleFeedViewsUpdatedAction);
        Actions.NewFeedCreated.addListener(this._handleNewFeed);
        Actions.NextPackagePageRequested.addListener(this._handleNextPageRequestedAction);
        Actions.PackageSelected.addListener(this._handlePackageSelectedAction);
        Actions.PackageListUpdated.addListener(this._handlePackageListUpdatedAction);
        Actions.PackageListedStatusChanged.addListener(this._handlePackageListedStatusChangedAction);
        Actions.MultiPromotePanelOpened.addListener(this._handleMultiPromotePanelOpenedAction);
        Actions.IvyPromotePanelOpened.addListener(this._handleIvyPromotePanelOpenedAction);
        Actions.MultiPromotePanelClosed.addListener(this._handleMultiPromotePanelClosedAction);
        Actions.MultiplePackagesPromoted.addListener(this._handlePackageVersionsPromotedThrougPanelAction);
        Actions.MultiCommandVersionDropdownClicked.addListener(this._handleMultiCommandVersionDropdownClickedAction);
        Actions.PackageSelectionChangedInPackageGrid.addListener(this._handlePackageSelectedInPackageGrid);
        Actions.PackagesFilterBarChanged.addListener(this._handlePackagesFilterBarChanged);
        Actions.PackageListedChanged.addListener(this._handlePackageListedChangedAction);
        SettingsActions.FeedSettingsNavigateClicked.addListener(this._handleFeedSettingsNavigateClicked);
        SettingsActions.FeedUpdated.addListener(this._handleFeedSettingsUpdatedAction);
        Actions.PackageVersionDeprecated.addListener(this._handlePackageVersionDeprecatedAction);
        Actions.PackagesDeleted.addListener(this._handlePackagesDeletedAction);
        Actions.ToggleCreateBadgePanel.addListener(this._handleToggleCreateBadgePanelAction);

        RecycleBinActions.NextPackagePageRequested.addListener(this._handleRecycleBinNextPageRequestedAction);
        RecycleBinActions.PackageSelected.addListener(this._handleRecyleBinPackageSelectedAction);
        RecycleBinActions.RecycleBinBreadCrumbClicked.addListener(this._handleRecycleBinBreadCrumbSelectedAction);
        RecycleBinActions.RecycleBinClicked.addListener(this._handleRecycleBinNavigateClickedAction);
        RecycleBinActions.FilterPackages.addListener(this._handleRecycleBinFilterPackagesAction);
        RecycleBinActions.EmptyRecycleBin.addListener(this._handleEmptyRecycleBinBackendAction);
        RecycleBinActions.EmptyDialogClosed.addListener(this._handleCloseRecycleBinModalDialog);
        RecycleBinActions.EmptyDialogOpened.addListener(this._handleOpenRecycleBinModalDialog);
        RecycleBinActions.PackagesRestoredToFeed.addListener(this._handlePackagesRestoredToFeed);

        ReduxActions.UpdateLegacyFeedSelection.addListener(this._handleReduxFeedSelectedAction);
    }

    // all listeners must be removed
    public dispose(): void {
        Actions.FeedBreadcrumbSelected.removeListener(this._handleFeedBreadCrumbSelectedAction);
        Actions.FeedListUpdated.removeListener(this._handleFeedListUpdatedAction);
        Actions.FeedSelected.removeListener(this._handleFeedSelectedAction);
        Actions.FeedUpdated.removeListener(this._handleFeedUpdatedAction);
        Actions.FeedViewsUpdated.removeListener(this._handleFeedViewsUpdatedAction);
        Actions.NewFeedCreated.removeListener(this._handleNewFeed);
        Actions.NextPackagePageRequested.removeListener(this._handleNextPageRequestedAction);
        Actions.PackageSelected.removeListener(this._handlePackageSelectedAction);
        Actions.PackageListUpdated.removeListener(this._handlePackageListUpdatedAction);
        Actions.PackageListedStatusChanged.removeListener(this._handlePackageListedStatusChangedAction);
        Actions.MultiPromotePanelOpened.removeListener(this._handleMultiPromotePanelOpenedAction);
        Actions.IvyPromotePanelOpened.removeListener(this._handleIvyPromotePanelOpenedAction);
        Actions.MultiPromotePanelClosed.removeListener(this._handleMultiPromotePanelClosedAction);
        Actions.MultiplePackagesPromoted.removeListener(this._handlePackageVersionsPromotedThrougPanelAction);
        Actions.MultiCommandVersionDropdownClicked.removeListener(this._handleMultiCommandVersionDropdownClickedAction);
        Actions.PackageSelectionChangedInPackageGrid.removeListener(this._handlePackageSelectedInPackageGrid);
        Actions.PackagesFilterBarChanged.removeListener(this._handlePackagesFilterBarChanged);
        Actions.PackageListedChanged.removeListener(this._handlePackageListedChangedAction);
        SettingsActions.FeedSettingsNavigateClicked.removeListener(this._handleFeedSettingsNavigateClicked);
        SettingsActions.FeedUpdated.removeListener(this._handleFeedSettingsUpdatedAction);
        Actions.PackageVersionDeprecated.removeListener(this._handlePackageVersionDeprecatedAction);
        Actions.PackagesDeleted.removeListener(this._handlePackagesDeletedAction);
        Actions.ToggleCreateBadgePanel.removeListener(this._handleToggleCreateBadgePanelAction);

        RecycleBinActions.NextPackagePageRequested.removeListener(this._handleRecycleBinNextPageRequestedAction);
        RecycleBinActions.PackageSelected.removeListener(this._handleRecyleBinPackageSelectedAction);
        RecycleBinActions.RecycleBinBreadCrumbClicked.removeListener(this._handleRecycleBinBreadCrumbSelectedAction);
        RecycleBinActions.RecycleBinClicked.removeListener(this._handleRecycleBinNavigateClickedAction);
        RecycleBinActions.FilterPackages.removeListener(this._handleRecycleBinFilterPackagesAction);
        RecycleBinActions.EmptyRecycleBin.removeListener(this._handleEmptyRecycleBinBackendAction);
        RecycleBinActions.EmptyDialogClosed.removeListener(this._handleCloseRecycleBinModalDialog);
        RecycleBinActions.EmptyDialogOpened.removeListener(this._handleOpenRecycleBinModalDialog);
        RecycleBinActions.PackagesRestoredToFeed.removeListener(this._handlePackagesRestoredToFeed);

        ReduxActions.UpdateLegacyFeedSelection.removeListener(this._handleReduxFeedSelectedAction);
    }

    private _initializeFeedData(pageContext: IVssPageContext, selectedFeed?: string, state?: IHubState): void {
        const feedsResult: FeedsResult = this._webPageDataService.getInitialFeedsResult();
        const result = feedsResult ? feedsResult.result : FeedRetrievalResult.Error;
        switch (result) {
            case FeedRetrievalResult.NoFeeds:
                this._currentState.feeds = [];
                this._currentState.displayWelcomeMessage = true;
                if (selectedFeed != null) {
                    Actions.ErrorEncountered.invoke({
                        message: Utils_String.format(PackageResources.Error_UrlFeedNotFound, selectedFeed)
                    } as IError);
                }
                break;
            case FeedRetrievalResult.NotFound:
                Actions.ErrorEncountered.invoke({
                    message: Utils_String.format(PackageResources.Error_UrlFeedNotFound, selectedFeed)
                } as IError);
                NavigationHandler.renderFeed(pageContext, feedsResult.selectedFeed as Feed);
            // no break, since server did return other feeds
            // tslint:disable-next-line:no-switch-case-fall-through
            case FeedRetrievalResult.Success:
                // this doesn't use FeedManager.
                this._currentState.selectedFeed = feedsResult.selectedFeed as Feed;

                // MRU view should be the view that's been set in the data provider
                this._currentState.mruViewId =
                    this._currentState.selectedFeed.view != null ? this._currentState.selectedFeed.view.id : null;

                if (feedsResult.selectedFeed.upstreamEnabled === false) {
                    this._handleUpstreamDisabled(state.upstreamSource);
                } else if (state.upstreamSource != null) {
                    const upstreamSource = urlSourceToUpstreamSource(
                        state,
                        this._currentState.selectedFeed.upstreamSources
                    );
                    this._currentState.selectedFeed.upstreamSource = upstreamSource;
                    if (upstreamSource === null) {
                        Actions.ErrorEncountered.invoke({
                            isCritical: false,
                            message: Utils_String.format(
                                PackageResources.Error_InvalidUpstreamSource,
                                state.upstreamSource
                            )
                        } as IError);
                    }
                }
                NavigationHandler.updateFeedHistoryOnLanding(this.pageContext, this._currentState.selectedFeed);
                this._currentState.feeds = feedsResult.feeds as Feed[];
                this._currentState.feedViews = feedsResult.feedViews;
                this._currentState.feedUser.isAnonymousOrPublic = PermissionHelper.isUserAnonymousOrPublic(
                    this.pageContext,
                    this._currentState.selectedFeed
                );
                break;
            case FeedRetrievalResult.Error:
            default:
                Actions.ErrorEncountered.invoke({
                    details: feedsResult ? feedsResult.errorMessage : "",
                    isCritical: true,
                    link: () => window.location.reload(),
                    linkText: PackageResources.Error_ReloadPageLink,
                    message: PackageResources.Error_ErrorLoadingFeedsInDataProvider
                } as IError);
                break;
        }
    }

    private _getPackageMetrics(packages: Package[]): void {
        if (!this._currentState.metricsEnabled) {
            return;
        }

        if (!packages || packages.length == 0) {
            return;
        }

        const packageIds = [];
        packages.forEach((pkg: Package) => {
            this._currentState.packageIdMetricsMap[pkg.id] = null;
            packageIds.push(pkg.id);
        });

        const feedId = this._getFullyQualifiedFeedId();
        const feedsDataService = this._feedsDataService;
        const projectId = this._currentState.selectedFeed.project ? this._currentState.selectedFeed.project.id : null;
        getPackageMetricsAsync(projectId, feedId, packageIds, feedsDataService).then(
            (packageIdMetrics: { [key: string]: PackageMetrics }) => {
                const packageIdMetricsIds = Object.keys(packageIdMetrics);
                packageIdMetricsIds.forEach(id => {
                    this._currentState.packageIdMetricsMap[id] = packageIdMetrics[id];
                });
                this.emitChanged();
            }
        );
    }

    private _initializeRecycleBinPackageListData(): void {
        const recycleBinPackagesResult = this._filterByEnabledProtocolDataService.getRecycleBinPackagesResult();

        switch (recycleBinPackagesResult.result) {
            case PackagesRetrievalResult.NoPackages:
                break;
            case PackagesRetrievalResult.Success:
                this.setRecycleBinPackages(recycleBinPackagesResult.packages);
                break;
            case PackagesRetrievalResult.Error:
            default:
                Actions.ErrorEncountered.invoke({
                    details: recycleBinPackagesResult.errorMessage,
                    isCritical: true,
                    link: () => window.location.reload(),
                    linkText: PackageResources.Error_ReloadPageLink,
                    message: PackageResources.Error_ErrorLoadingPackageListInDataProvider
                } as IError);
                break;
        }
    }

    private initializeAvailableProtocols(): void {
        this._currentState.protocolMap = this.ProtocolProvider.getEnabledProtocolTypes();
    }

    private get PerformanceService(): IFeedPerformanceService {
        return this.pageContext.getService<IFeedPerformanceService>("IFeedPerformanceService");
    }

    private _handleFeedBreadCrumbSelectedAction = (packageSummary?: Package): void => {
        CustomerIntelligenceHelper.publishEvent(this.pageContext, CiConstants.FeedBreadCrumbClicked);
        const breadCrumbSelectedScenario = this.PerformanceService.startScenario(PerfScenarios.BreadCrumbSelected);

        if (this._currentState.selectedFeed.deletedDate == null) {
            this._updatePackageList(breadCrumbSelectedScenario);
            this._correctLatestVersion(packageSummary);
        }

        this._reduxStore.dispatch(FeedHubActions.viewFeed());
    };

    private _handleRecycleBinBreadCrumbSelectedAction = (): void => {
        CustomerIntelligenceHelper.publishEvent(this.pageContext, CiConstants.RecycleBinBreadCrumbClicked);
        const breadCrumbSelectedScenario = this.PerformanceService.startScenario(PerfScenarios.BreadCrumbSelected);
        this._navigateToRecycleBin();
        this._updateRecycleBinPackagesList(breadCrumbSelectedScenario);
    };

    private _handleRecycleBinNavigateClickedAction = (): void => {
        CustomerIntelligenceHelper.publishEvent(this.pageContext, CiConstants.RecycleBinButtonClicked);
        const recycleBinClickedScenario = this.PerformanceService.startScenario(
            PerfScenarios.FeedRecycleBinSelected,
            false
        );
        this._navigateToRecycleBin();
        this._updateRecycleBinPackagesList(recycleBinClickedScenario);
    };

    private _handleRecycleBinFilterPackagesAction = (packageNameFilterValue: string): void => {
        this._currentState.recycleBinState.recycleBinPackages = [];
        this._currentState.recycleBinState.packageNameFilterValue = packageNameFilterValue || null;
        if (packageNameFilterValue) {
            // We don't want to be filtering on both the feed and recycle bin views at the same time - messes up the page message
            this._filterText = null;
        }

        this._loadRecycleBinPackages();
    };

    private _handleRecycleBinNextPageRequestedAction = (): void => {
        if (this._currentState.nextPageLoading) {
            return;
        }

        const scenario = this.PerformanceService.startScenario(PerfScenarios.RecycleBinPackagesPageLoad);

        this._loadRecycleBinPackages().then(
            () => this.PerformanceService.endScenario(scenario),
            error => this.PerformanceService.endScenario(scenario)
        );
    };

    private _loadRecycleBinPackages(): Promise<void> {
        const state = this._currentState;
        const top = state.pageSize;
        const skip = state.recycleBinState.recycleBinPackages ? state.recycleBinState.recycleBinPackages.length : null;
        const packageNameFilter = state.recycleBinState.packageNameFilterValue || null;

        CustomerIntelligenceHelper.publishEvent(this.pageContext, CiConstants.RecycleBinPackagesPageRequested, {
            totalPackagesRequested: top + skip
        });

        const loadingPromise = this._filterByEnabledProtocolDataService
            .getRecycleBinPackagesAsync(state.selectedFeed, top, skip, packageNameFilter)
            .then(skip ? this.appendRecycleBinPackages : this.setRecycleBinPackages, (error: Error) => {
                Actions.ErrorEncountered.invoke({
                    message: error.message,
                    isCritical: true
                } as IError);
            })
            .then(() => {
                state.packagesLoading = state.nextPageLoading = false;
                this.emitChanged();
            });

        skip === 0 ? (state.packagesLoading = true) : (state.nextPageLoading = true);
        this.emitChanged();

        return loadingPromise;
    }

    private _handleReduxFeedSelectedAction = async (fullyQualifiedFeedId: string): Promise<void> => {
        const selectedFeed = this._currentState.feeds.find(
            feed => feed.fullyQualifiedId == fullyQualifiedFeedId || feed.id == fullyQualifiedFeedId
        );
        this._handleFeedSelectedAction(selectedFeed);
    };

    private _handleFeedSelectedAction = async (feed: Feed): Promise<void> => {
        CustomerIntelligenceHelper.publishEvent(this.pageContext, CiConstants.FeedSelected);
        const feedSelectedScenario = this.PerformanceService.startScenario(PerfScenarios.FeedSelected);
        try {
            this._currentState.packagesLoading = true;
            this.emitChanged();
            await this.setCurrentFeed(feed, false /*add history*/);
        } finally {
            this.emitChanged();
        }
        this.PerformanceService.endScenario(feedSelectedScenario);
    };

    private async setCurrentFeed(feed: Feed, updateHistory: boolean): Promise<void> {
        try {
            await this.feedManager.setFeed(feed);

            let viewFeedAction = FeedHubActions.viewFeed();
            if (updateHistory) {
                viewFeedAction = replaceHistoryPoint(viewFeedAction);
            }
            this._reduxStore.dispatch(viewFeedAction);
            if (feed.deletedDate === null) {
                this._getPackageMetrics(this._currentState.packages);
            }
        } catch (error) {
            Actions.ErrorEncountered.invoke({
                message: error.message,
                isCritical: true
            } as IError);
        } finally {
            this._currentState.packagesLoading = false;
        }
    }

    // used by new settings experience
    // TODO: This should not be getting called by the other action.
    // feeds array should contain reference to current feed, so when current feed is updated in SaveChangesHandler, feeds array is also updated
    private _handleFeedSettingsUpdatedAction = (feed: Feed): void => {
        // Currently you're able to update following fields:
        const tempFeed = this._currentState.selectedFeed;
        tempFeed.name = feed.name ? feed.name : this._currentState.selectedFeed.name;
        tempFeed.description =
            feed.description || feed.description === ""
                ? feed.description
                : this._currentState.selectedFeed.description;
        tempFeed.hideDeletedPackageVersions =
            feed.hideDeletedPackageVersions != null ? feed.hideDeletedPackageVersions : false;
        tempFeed.badgesEnabled = feed.badgesEnabled != null ? feed.badgesEnabled : false;
        tempFeed.upstreamEnabled =
            feed.upstreamEnabled != null ? feed.upstreamEnabled : this._currentState.selectedFeed.upstreamEnabled;
        tempFeed.upstreamSources = feed.upstreamSources
            ? feed.upstreamSources
            : this._currentState.selectedFeed.upstreamSources;
        tempFeed.upstreamSource = feed.upstreamSource
            ? feed.upstreamSource
            : this._currentState.selectedFeed.upstreamSource;

        this._currentState.selectedFeed = tempFeed;

        // update feeds array
        const index = findIndex(this._currentState.feeds, (existingFeed: Feed) => {
            return existingFeed.id === tempFeed.id;
        });
        // update feed in redux store
        this._reduxStore.dispatch(FeedHubActions.feedUpdated(feed));

        if (index > -1) {
            this._currentState.feeds[index] = tempFeed;
            this._currentState.feeds = this._currentState.feeds.sort(this._feedSort);
        }

        this.emitChanged();
    };

    private _handleFeedUpdatedAction = (feed: Feed): void => {
        CustomerIntelligenceHelper.publishEvent(this.pageContext, CiConstants.FeedUpdated);
        const feedUpdatedScenario = this.PerformanceService.startScenario(PerfScenarios.FeedUpdated, false);
        const previousUpstreamEnabled: boolean = this._currentState.selectedFeed.upstreamEnabled;

        // Currently you're able to update following fields:
        const tempFeed = this._currentState.selectedFeed;
        tempFeed.name = feed.name ? feed.name : this._currentState.selectedFeed.name;
        tempFeed.description =
            feed.description || feed.description === ""
                ? feed.description
                : this._currentState.selectedFeed.description;
        tempFeed.hideDeletedPackageVersions =
            feed.hideDeletedPackageVersions != null
                ? feed.hideDeletedPackageVersions
                : this._currentState.selectedFeed.hideDeletedPackageVersions;
        tempFeed.upstreamEnabled =
            feed.upstreamEnabled != null ? feed.upstreamEnabled : this._currentState.selectedFeed.upstreamEnabled;
        tempFeed.upstreamSources = feed.upstreamSources
            ? feed.upstreamSources
            : this._currentState.selectedFeed.upstreamSources;
        tempFeed.upstreamSource = feed.upstreamSource
            ? feed.upstreamSource
            : this._currentState.selectedFeed.upstreamSource;
        tempFeed.capabilities = feed.capabilities ? feed.capabilities : this._currentState.selectedFeed.capabilities;

        this._currentState.selectedFeed = tempFeed;

        // If the new upstream source is different, refetch the packages
        if (previousUpstreamEnabled !== this._currentState.selectedFeed.upstreamEnabled) {
            const perfScenario: IVssScenario = this._handleUpstreamSourceUpdates(
                this._currentState.selectedFeed.upstreamSource
            );
            if (perfScenario) {
                this._filterByEnabledProtocolDataService
                    .getPackagesAsync(
                        this._currentState.selectedFeed,
                        this._currentState.pageSize, // top
                        null, // skip default 0
                        this._includeDeletedPackages(),
                        null, // isListed
                        this._filterText,
                        true // includeDescription
                    )
                    .then(
                        (packages: Package[]) => {
                            this.setCurrentStatePackages(packages, this._currentState.pageSize, true);
                            this._finishNextPageLoadingEvent();
                            this._currentState.packagesLoading = false;
                            this.emitChanged();

                            this.PerformanceService.endScenario(perfScenario);

                            return packages;
                        },
                        err => {
                            Actions.ErrorEncountered.invoke({
                                message: err.message,
                                isCritical: true,
                                details: err
                            } as IError);
                        }
                    )
                    .then((packages: Package[]) => {
                        this._getPackageMetrics(packages);
                    });
            }
        }

        for (let i = 0; i < this._currentState.feeds.length; i++) {
            if (this._currentState.feeds[i].id === feed.id) {
                this._currentState.feeds[i] = tempFeed;
            }
        }

        this.PerformanceService.endScenario(feedUpdatedScenario);

        this.emitChanged();
    };

    private _handleFeedRestoredAction = async (feed: Feed): Promise<void> => {
        const feedRestoredScenario = this.PerformanceService.startScenario(PerfScenarios.FeedRestored);
        try {
            this._currentState.packagesLoading = true;
            this.emitChanged();
            feed.deletedDate = null;
            feed.scheduledPermanentDeleteDate = null;
            await this.setCurrentFeed(feed, false /*add history*/);
            this._reduxStore.dispatch(FeedHubActions.feedRestored(this._currentState.selectedFeed.id));
        } finally {
            this.emitChanged();
        }
        this.PerformanceService.endScenario(feedRestoredScenario);
    };

    private _handleFeedPermanentlyDeletedAction = async (deletedFeed: Feed): Promise<void> => {
        CustomerIntelligenceHelper.publishEvent(this.pageContext, CiConstants.FeedPermanentDeleted);
        const feedPDeletedScenario = this.PerformanceService.startScenario(PerfScenarios.FeedPermananentDeleted);

        for (let i = 0; i < this._currentState.feeds.length; i++) {
            if (this._currentState.feeds[i].id === deletedFeed.id) {
                this._currentState.feeds.splice(i, 1);
                break;
            }
        }
        this._reduxStore.dispatch(FeedHubActions.feedPermanentDeleted(this._currentState.selectedFeed.id));
        if (this._currentState.feeds[0]) {
            this._currentState.packagesLoading = true;
            this.emitChanged();
            await this.setCurrentFeed(this._currentState.feeds[0], true /*updateHistory*/);
        } else {
            this._currentState.displayWelcomeMessage = true;

            const state: IHubState = {
                action: HubActionStrings.ViewFeed,
                feed: null
            } as IHubState;
            this._reduxStore.dispatch(NavigationActions.navigateToPage(state));

            document.title = PackageResources.HubTitle;

            // not the right method<name> to call
            this.setHubState(HubActionStrings.ViewFeed, state);
        }

        this.PerformanceService.endScenario(feedPDeletedScenario);

        this.emitChanged();
    };

    private _handleFeedDeletedAction = async (deletedFeed: Feed): Promise<void> => {
        CustomerIntelligenceHelper.publishEvent(this.pageContext, CiConstants.FeedDeleted);
        const feedDeletedScenario = this.PerformanceService.startScenario(PerfScenarios.DeleteFeed);

        let currentFeed: Feed = null;
        for (let i = 0; i < this._currentState.feeds.length; i++) {
            if (this._currentState.feeds[i].id === deletedFeed.id) {
                currentFeed = this._currentState.feeds[i];

                currentFeed.deletedDate = new Date();
                currentFeed.scheduledPermanentDeleteDate = new Date();
                currentFeed.scheduledPermanentDeleteDate.setDate(
                    currentFeed.scheduledPermanentDeleteDate.getDate() + 30
                );
                this._currentState.feeds[i] = currentFeed;
                break;
            }
        }

        this._reduxStore.dispatch(FeedHubActions.feedDeleted(currentFeed.id));

        // Always navigate to the first feed in the array
        this._currentState.packagesLoading = true;
        this.emitChanged();
        await this.setCurrentFeed(this._currentState.feeds[0], true /*updateHistory*/);

        this.PerformanceService.endScenario(feedDeletedScenario);

        this.emitChanged();
    };

    private _handleFeedViewsUpdatedAction = (views: FeedView[]): void => {
        CustomerIntelligenceHelper.publishEvent(this.pageContext, CiConstants.FeedViewsUpdated);
        this._currentState.feedViews = views;

        if (
            findIndex(this._currentState.feedViews, (view: FeedView) => view.id === this._currentState.mruViewId) === -1
        ) {
            this._currentState.mruViewId = this._currentState.feedViews[0].id;
        }

        // In case of deleting or renaming views, the view bubbles in the package list need to be updated
        if (this._currentState.packages) {
            this._currentState.packagesLoading = true;
            const directUpstreamSourceId: string = this._currentState.selectedFeed.upstreamSource
                ? this._currentState.selectedFeed.upstreamSource.id
                : null;
            const projectId = this._currentState.selectedFeed.project
                ? this._currentState.selectedFeed.project.id
                : null;
            this._filterByEnabledProtocolDataService
                .getPackagesAsync(
                    this._currentState.selectedFeed,
                    this._currentState.pageSize, // top
                    null, // skip default 0
                    this._includeDeletedPackages(),
                    null, // isListed
                    this._filterText,
                    true // includeDescription
                )
                .then(
                    (packages: Package[]) => {
                        this.setCurrentStatePackages(packages, this._currentState.pageSize, true);
                        this._currentState.packagesLoading = false;
                        this.emitChanged();

                        return packages;
                    },
                    err => {
                        Actions.ErrorEncountered.invoke({
                            message: err.message,
                            isCritical: true,
                            details: err
                        } as IError);
                    }
                )
                .then((packages: Package[]) => {
                    this._getPackageMetrics(packages);
                });
        }

        this.emitChanged();
    };

    private _handleNextPageRequestedAction = (): void => {
        const packagesPageLoadScenario = this.PerformanceService.startScenario(PerfScenarios.PackagesPageLoad, false);

        if (this._currentState.nextPageLoading) {
            return;
        }

        this._currentState.nextPageLoading = true;

        const top = this._currentState.pageSize;
        const skip = this._currentState.packages ? this._currentState.packages.length : null;
        const protocol: string = getProtocolFromUpstreamSource(this._currentState.selectedFeed);
        CustomerIntelligenceHelper.publishEvent(this.pageContext, CiConstants.PackagesPageRequested, {
            totalPackagesRequested: top + skip,
            protocol
        });
        const directUpstreamSourceId: string = this._currentState.selectedFeed.upstreamSource
            ? this._currentState.selectedFeed.upstreamSource.id
            : null;
        const projectId = this._currentState.selectedFeed.project ? this._currentState.selectedFeed.project.id : null;

        this._filterByEnabledProtocolDataService
            .getPackagesAsync(
                this._currentState.selectedFeed,
                top,
                skip,
                this._includeDeletedPackages(),
                null, // isListed
                this._filterText,
                true // includeDescription
            )
            .then(
                (packages: Package[]) => {
                    if (packages) {
                        const pkgs = this._currentState.packages.concat(
                            PackageModifiedCache.applyCacheByPackageList(packages)
                        );

                        this.setCurrentStatePackages(pkgs, top + skip);
                    }

                    this._currentState.nextPageLoading = false;
                    this.emitChanged();
                    this.PerformanceService.endScenario(packagesPageLoadScenario);

                    return packages;
                },
                err => {
                    Actions.ErrorEncountered.invoke({
                        message: err.message,
                        isCritical: true,
                        details: err
                    } as IError);
                }
            )
            .then((packages: Package[]) => {
                this._getPackageMetrics(packages);
            });

        this.emitChanged();
    };

    private _handleMultiPromotePanelOpenedAction = (payload: IPromotePanelPayload) => {
        CustomerIntelligenceHelper.publishEvent(this.pageContext, CiConstants.MultiPromotePanelOpened);
        // Get MRU view before opening the panel if it's not already set.
        if (!this._currentState.mruViewId) {
            const settingsDataService: SettingsDataService = this.pageContext.getService<SettingsDataService>(
                "IFeedSettingsDataService"
            );
            settingsDataService.getPackageSettingsAsync().then(
                result => {
                    if (result && result[SettingsConstants.MruFeedId]) {
                        const mruFeedId = result[SettingsConstants.MruFeedId];
                        const index = mruFeedId.indexOf("@");
                        if (index !== -1) {
                            this._currentState.mruViewId = mruFeedId.split("@")[1].id;
                        } else {
                            this._currentState.mruViewId = this._currentState.feedViews[0].id;
                        }
                    } else {
                        this._currentState.mruViewId = this._currentState.feedViews[0].id;
                    }
                    this._handleMultiPromoteOpened(payload);
                },
                err => {
                    this._currentState.mruViewId = this._currentState.feedViews[0].id;
                    this._handleMultiPromoteOpened(payload);
                }
            );
        } else {
            this._handleMultiPromoteOpened(payload);
        }
    };

    private _handleIvyPromotePanelOpenedAction = (payload: IPackagePayload) => {
        this._reduxStore.dispatch(
            FeedHubActions.showIvyPromote({
                packageSummary: payload.packageSummary,
                packageVersion: payload.packageVersion
            })
        );
    };

    private _handleMultiPromoteOpened = (payload: IPromotePanelPayload) => {
        if (payload.selectedPackages.length > 0) {
            this._currentState.isMultiPromotePanelOpen = true;
            this._currentState.selectedPackages = payload.selectedPackages;
            this.emitChanged();
        }
    };

    private _handleMultiPromotePanelClosedAction = () => {
        this._currentState.isMultiPromotePanelOpen = false;
        this._currentState.multiCommandDropdownVersions = null;

        this.emitChanged();
    };

    private _handleToggleCreateBadgePanelAction = (openPanel: boolean): void => {
        this._currentState.showCreateBadgePanel = openPanel;
        this.emitChanged();

        openPanel && CustomerIntelligenceHelper.publishEvent(this.pageContext, CiConstants.PackageBadgePanelOpened);
    };

    private _handlePackageSelectedInPackageGrid = (payload: IPackageSelectionChangedPayload) => {
        this._currentState.selectedPackages = payload.selectedPackages;
        this.emitChanged();
    };

    private _handleFilterTextUpdates = (newFilterText: string): IVssScenario => {
        if ((!newFilterText && !this._filterText) || newFilterText === this._filterText) {
            // The user either tried to clear no filter or is submitting the same filter again. Ignore.
            return undefined;
        }

        const perfScenario = this.PerformanceService.startScenario(PerfScenarios.FilterPackages, false);

        CustomerIntelligenceHelper.publishEvent(this.pageContext, CiConstants.PackagesFiltered);
        // this._currentState.packagesLoading = true;

        return perfScenario;
    };

    private _handleUpstreamSourceUpdates = (nextSourceKey: UpstreamSource): IVssScenario => {
        // If the upstream source hasn't changed then do nothing
        if (this._currentState.selectedFeed.upstreamSource === nextSourceKey) {
            return undefined;
        }

        const perfScenario = this.PerformanceService.startScenario(PerfScenarios.UpstreamSourceUpdated, false);

        CustomerIntelligenceHelper.publishEvent(this.pageContext, CiConstants.UpstreamSourceUpdated);
        this._currentState.selectedFeed.upstreamSource = nextSourceKey;

        return perfScenario;
    };

    private _handleSelectedFeedViewUpdates = (nextView: FeedView): IVssScenario => {
        const currentSavedViewId: string = this._currentState.selectedFeed.view
            ? this._currentState.selectedFeed.view.id
            : undefined;
        nextView = PackageFilterBarHelper.getFeedViewFromObject(nextView);
        const nextViewId: string = nextView ? nextView.id : undefined;

        // If trying to clear the view filter, and they don't have access to base feed, then do nothing
        if (!nextView && !PermissionHelper.hasAccessToBaseFeed(this._currentState.selectedFeed)) {
            return;
        }

        // If updating to the currently selected view, do nothing
        if (currentSavedViewId === nextViewId) {
            return undefined;
        }

        // If there is no active view, and requesting the "All" view then do nothing
        if (!currentSavedViewId && nextView.id === PackageResources.FeedViewDropdown_AllViews_Key) {
            return undefined;
        }

        CustomerIntelligenceHelper.publishEvent(this.pageContext, CiConstants.FeedViewSelected);

        // If the "All" view is requested, set view to null
        if (nextView && nextView.id === PackageResources.FeedViewDropdown_AllViews_Key) {
            nextView = undefined;
        }

        const perfScenario = this.PerformanceService.startScenario(PerfScenarios.FeedViewSelected, false);

        this._currentState.selectedFeed.view = nextView;
        this._reduxStore.dispatch(
            FeedHubActions.feedSelected(getFullyQualifiedFeedId(this._currentState.selectedFeed))
        );
        if (nextView) {
            const viewName = this._parseViewName(nextView.name);
            this._currentState.selectedFeed.view.name = viewName;
        }

        return perfScenario;
    };

    private _handlePackagesFilterBarChanged = (payload: IFilterState): void => {
        const nextFilterText: string = PackageFilterBarHelper.getKeywordFilterText(payload);
        const nextSourceKey: UpstreamSource = filterStateToUpstreamSource(
            payload,
            this._currentState.selectedFeed.upstreamSources
        );
        const nextView: FeedView = this._getFilterFeedView(payload);
        const filterScenario = this._handleFilterTextUpdates(nextFilterText);
        const upstreamScenario = this._handleUpstreamSourceUpdates(nextSourceKey);
        const viewScenario = this._handleSelectedFeedViewUpdates(nextView);
        const overallPerfScenario: IVssScenario[] = [filterScenario, upstreamScenario, viewScenario].filter(
            value => !!value
        );

        const atLeastOneUpdate: boolean = overallPerfScenario.length > 0;
        const wasFilterUpdate: boolean = !!filterScenario;
        const filterQueryString: string = wasFilterUpdate ? nextFilterText : this._filterText;

        if (atLeastOneUpdate) {
            const showDeleted: boolean = this._includeDeletedPackages();
            const directUpstreamSourceId: string = this._currentState.selectedFeed.upstreamSource
                ? this._currentState.selectedFeed.upstreamSource.id
                : null;

            this._currentState.packagesLoading = true;
            NavigationHandler.updateUrlWithFilterValues(this.pageContext, this._currentState.selectedFeed);

            const projectId = this._currentState.selectedFeed.project
                ? this._currentState.selectedFeed.project.id
                : null;
            this._filterByEnabledProtocolDataService
                .getPackagesAsync(
                    this._currentState.selectedFeed,
                    this._currentState.pageSize, // top
                    null, // skip default 0
                    showDeleted,
                    null, // isListed
                    filterQueryString,
                    true // includeDescription
                )
                .then(
                    (packages: Package[]) => {
                        this.setCurrentStatePackages(packages, this._currentState.pageSize, true);
                        if (this._currentState.selectedPackages.length > 0) {
                            const newSelectedPackages: Package[] = [];
                            this._currentState.selectedPackages.forEach((selectedPackage: Package) => {
                                const indexFound = findIndex(
                                    this._currentState.packages,
                                    (pkg: Package) => pkg.id === selectedPackage.id
                                );
                                if (indexFound !== -1) {
                                    newSelectedPackages.push(selectedPackage);
                                }
                            });
                            this._currentState.selectedPackages = newSelectedPackages;
                        }

                        this._currentState.packagesLoading = false;
                        this._finishNextPageLoadingEvent();
                        overallPerfScenario.forEach(scenario => this.PerformanceService.endScenario(scenario));

                        const announcement = buildAnnouncementPackageFilterResults(
                            packages,
                            this._currentState.pageSize
                        );
                        announce(announcement);

                        // Set MRU view
                        const nextMru: string =
                            nextView != null
                                ? Utils_String.format("{0}@{1}", this._currentState.selectedFeed.id, nextView.id)
                                : this._currentState.selectedFeed.id;
                        const settingsDataService: SettingsDataService = this.pageContext.getService<SettingsDataService>(
                            "IFeedSettingsDataService"
                        );
                        settingsDataService.setMruFullyQualifiedFeedId(nextMru);

                        this.emitChanged();

                        return packages;
                    },
                    err => {
                        Actions.ErrorEncountered.invoke({
                            message: err.message,
                            isCritical: true,
                            details: err
                        } as IError);
                    }
                )
                .then((packages: Package[]) => {
                    this._getPackageMetrics(packages);
                });
        }

        // If the view wasn't changed, and the user does not have access to the feed, leave the value as it was
        if (!viewScenario && !PermissionHelper.hasAccessToBaseFeed(this._currentState.selectedFeed)) {
            const newState = PackageFilterBarHelper.setFeedViewToFilterState(
                payload,
                this._currentState.selectedFeed.view
            );

            // suppress the event, since we don't want to get in a loop
            this._currentState.hubState.filter.setState(newState, true /*suppressChangeEvent*/);
        }

        this._filterText = nextFilterText;
        this.emitChanged();
    };

    private _handlePackageVersionDeprecatedAction = (payload: IPackageDeprecatedPayload): void => {
        if (payload.selectedVersion && payload.selectedPackages && payload.selectedPackages.length === 1) {
            // This is the handling for the single dialog.
            const index = findIndex(
                this._currentState.packages,
                (pkg: Package) => pkg.id === payload.selectedPackages[0].id
            );
            const version = this._currentState.packages[index].versions[0] as PackageVersion;
            if (version.protocolMetadata && version.protocolMetadata.data) {
                version.protocolMetadata.data.deprecated = payload.message;
            }

            this.emitChanged();
            return;
        }

        if (payload.selectedPackages) {
            CustomerIntelligenceHelper.publishEvent(this.pageContext, CiConstants.MultiplePackageVersionsDeprecated);
            const deprecateScenario = this.PerformanceService.startScenario(
                PerfScenarios.MultiplePackageVersionsDeprecated
            );
            const npmPackageDetails = [];

            payload.selectedPackages.forEach((pkg: Package) => {
                npmPackageDetails.push({
                    id: pkg.name,
                    version: pkg.versions[0].version
                });
            });

            const npmDataService = this.pageContext.getService<INpmDataService>("INpmDataService");
            const deprecatePackageVersionDetails = {
                data: { message: payload.message ? payload.message : "" } as BatchDeprecateData,
                operation: NpmBatchOperationType.Deprecate,
                packages: npmPackageDetails
            } as NpmPackagesBatchRequest;

            let announceStart = PackageResources.NpmDeprecate_ProgressStarted;
            let announceEnd = PackageResources.NpmDeprecate_ProgressEnded;
            let announceError = PackageResources.NpmDeprecate_ProgressFailed;
            if (payload.message == null) {
                announceStart = PackageResources.NpmDeprecate_ProgressEnded_Undeprecate;
                announceEnd = PackageResources.NpmDeprecate_ProgressEnded_Undeprecate;
                announceError = PackageResources.NpmDeprecate_ProgressFailed_Undeprecate;
            }

            const updatePromise = npmDataService
                .updatePackages(deprecatePackageVersionDetails, this._currentState.selectedFeed)
                .then(
                    success => {
                        // Update latest version
                        this._currentState.selectedPackages.forEach((pkg: Package) => {
                            const version = pkg.versions[0] as PackageVersion;
                            if (version.protocolMetadata && version.protocolMetadata.data) {
                                version.protocolMetadata.data.deprecated = payload.message;
                            }
                        });

                        this.emitChanged();

                        Actions.PackageVersionDeprecatedCompleted.invoke({}); // See ***Note*** above
                        this.PerformanceService.endScenario(deprecateScenario);
                    },
                    err => {
                        Actions.PackageVersionDeprecatedCompleted.invoke({}); // See ***Note*** above
                        this.PerformanceService.endScenario(deprecateScenario);

                        if (err.status === 403) {
                            Actions.ErrorEncountered.invoke({
                                message: PackageResources.Error_ErrorUserUnauthorized,
                                details: err
                            } as IError);
                        } else {
                            Actions.ErrorEncountered.invoke({
                                message: err.message,
                                isCritical: true,
                                details: err
                            } as IError);
                        }
                    }
                );

            ProgressAnnouncer.forPromise(updatePromise, {
                announceStartMessage: announceStart,
                announceEndMessage: announceEnd,
                announceErrorMessage: announceError
            });
        }
    };

    private _handlePackagesDeletedAction = async (payload: IPackageDeletedPayload): Promise<void> => {
        if (!payload.selectedPackages || (payload.selectedVersions && payload.selectedVersions.length !== 0)) {
            // Event is actually handled by the PackageStore in this case
            return;
        }

        const feed = this._currentState.selectedFeed;
        const packages = payload.selectedPackages;
        const singlePackage = packages.length === 1;

        CustomerIntelligenceHelper.publishEvent(
            this.pageContext,
            singlePackage ? CiConstants.PackageVersionDeleted : CiConstants.MultiplePackageVersionsDeleted
        );
        const perfScenario = this.PerformanceService.startScenario(
            singlePackage ? PerfScenarios.PackageVersionDeleted : PerfScenarios.MultiplePackageVersionsDeleted
        );
        perfScenario.properties["protocolType"] = packages[0].protocolType;

        try {
            const packagesByProtocolType = partition(packages, pkg => pkg.protocolType);
            const batchOperations = Object.keys(packagesByProtocolType).map(async protocolType => {
                try {
                    const provider = this.ProtocolProvider.get(protocolType, true);
                    const projectId = getProjectIdForProtocol(feed, provider);
                    await provider.deleteLatestVersions(projectId, feed, packagesByProtocolType[protocolType]);
                } catch (error) {
                    throw new Error(protocolType);
                }
            });

            await Promise.all(batchOperations);

            // Lie to the user that the packages were already deleted
            const selectedPackageIds: string[] = [];
            const recycleBinPackages = this._currentState.recycleBinState.recycleBinPackages;
            payload.selectedPackages.forEach(pkg => {
                const firstVersion = pkg.versions[0];
                firstVersion.isDeleted = true;
                firstVersion.isListed = false;

                if (recycleBinPackages) {
                    recycleBinPackages.push(pkg);
                }
                selectedPackageIds.push(pkg.id);
            });
            // if hiding deleted packages, latest version might have changed
            // this is best-effort, since deletion may not have been processed on the server yet
            if (this._currentState.selectedFeed.hideDeletedPackageVersions) {
                this._handlePackageListUpdatedAction(selectedPackageIds);
            }

            this._currentState.selectedPackages = [];
            this.emitChanged();
        } catch (error) {
            CustomerIntelligenceHelper.publishEvent(this.pageContext, CiConstants.BatchRequestFailed);
            Actions.ErrorEncountered.invoke({
                message: Utils_String.format(PackageResources.DeleteDialog_MultiDeleteFailed, error.message),
                isCritical: true
            } as IError);
        } finally {
            this.PerformanceService.endScenario(perfScenario);
            Actions.PackageDeletedCompleted.invoke({}); // See ***Note*** above
        }
    };

    private _handleCloseRecycleBinModalDialog = (): void => {
        this._currentState.recycleBinState.dialogOpen = false;
        this._currentState.recycleBinState.dialogProcessing = false;
        this.emitChanged();
    };

    private _handleOpenRecycleBinModalDialog = (): void => {
        this._currentState.recycleBinState.dialogOpen = true;
        this.emitChanged();
    };

    private _handleEmptyRecycleBinBackendAction = async (): Promise<void> => {
        // set the dialog to loading state.
        this._currentState.recycleBinState.dialogProcessing = true;
        this.emitChanged();

        CustomerIntelligenceHelper.publishEvent(this.pageContext, CiConstants.EmptyRecycleBin);
        const emptyRecycleBinScenario: IVssScenario = this.PerformanceService.startScenario(
            PerfScenarios.EmptyRecycleBin
        );

        const messageService = this.pageContext.getService<IGlobalMessagesService>("IGlobalMessagesService");
        let message = PackageResources.RecycleBin_ErrorToast;
        try {
            const opReference = await this.callEmptyRecycleBin();
            await this.startPollingForJobStatus(opReference);
            message = PackageResources.RecycleBin_SuccessToast;
        }
        catch (error) {
            Actions.ErrorEncountered.invoke({
                message: error.message,
                isCritical: true
            } as IError);
        }
        finally {
            const state = this._currentState.recycleBinState;
            state.dialogProcessing = false;
            if (state.dialogOpen) {
                state.dialogOpen = false;
                messageService.addToast({message, duration: 4000});
            }
            this.PerformanceService.endScenario(emptyRecycleBinScenario);
            this._currentState.selectedPackages = [];
            this.emitChanged();
        }
    };

    private callEmptyRecycleBin(): Promise<Operations.OperationReference> {
        const { selectedFeed } = this._currentState;
        return this._recycleBinDataService.emptyRecycleBinAsync(selectedFeed);
    }

    private startPollingForJobStatus(opReference: Operations.OperationReference): Promise<void> {

        const operationRestClient = this.pageContext.getRestClient<OperationRestClientGetter.IOperationsRestClient>("IOperationsRestClient", {
            serviceInstanceType: FeedServiceInstanceId
        } as IVssRestClientOptions);

        const isDialogOpen = () => {
            return this._currentState.recycleBinState.dialogOpen;
        }

        return new Promise<void>((resolve, reject) => {

            const intervalJob = setInterval(checkOperationStatus, 3000);

            async function checkOperationStatus() {
                try {
                    if (!isDialogOpen()) {
                        clearInterval(intervalJob);
                        resolve();
                        return;
                    }

                    const operation = await operationRestClient.getOperation(opReference.id);
                    switch (operation?.status) {
                        case Operations.OperationStatus.NotSet:
                        case Operations.OperationStatus.Queued:
                        case Operations.OperationStatus.InProgress:
                            break;

                        case Operations.OperationStatus.Succeeded:
                            clearInterval(intervalJob);
                            if (operation.resultMessage != null) {
                                const resultMessage = JSON.parse(operation.resultMessage);
                                // Operation API returns success for partial success.
                                // If one protocol failed, there was a failure.
                                if (resultMessage?.FailedProtocols?.length > 0) {
                                    reject(new Error(Utils_String.format(PackageResources.RecycleBin_EmptyRecycleBinFailed, resultMessage.FailedProtocols)));
                                    break;
                                }
                            }
                            resolve();
                            break;

                        case Operations.OperationStatus.Failed:
                        case Operations.OperationStatus.Cancelled:
                            clearInterval(intervalJob);
                            reject(new Error(PackageResources.RecycleBin_JobWasFailedOrCanceled));
                            break;
                    }
                }
                catch (err) {
                    clearInterval(intervalJob);
                    reject(err.message);
                }
            }
        });
    }

    private _handlePackageVersionsPromotedThrougPanelAction = async (
        payload: IPackagePromotedPayload
    ): Promise<void> => {
        const { selectedFeed, packages } = this._currentState;
        const { minimalPackageDetails, promotedView } = payload;

        const scenario = this._addPromoteTelemetry(payload);
        try {
            const errorProtocols: string[] = [];
            const successProtocols: string[] = [];
            const packagesByProtocolType = partition(minimalPackageDetails, pkg => pkg.protocolType);
            const batchOperations = Object.keys(packagesByProtocolType).map(async protocolType => {
                try {
                    const provider = this.ProtocolProvider.get(protocolType, true);
                    const projectId = getProjectIdForProtocol(selectedFeed, provider);
                    await provider.promotePackageVersions(
                        projectId,
                        selectedFeed,
                        promotedView,
                        packagesByProtocolType[protocolType]
                    );
                    successProtocols.push(protocolType);
                } catch (error) {
                    errorProtocols.push(protocolType);
                }
            });

            await Promise.all(batchOperations);

            // Did any protocol fail?
            if (errorProtocols.length) {
                const message = Utils_String.format(
                    PackageResources.MultiPromotePanel_PromoteFailed_Many,
                    errorProtocols.join(", ")
                );
                throw new Error(message);
            }

            // Lie to the user
            successProtocols.forEach(protocolType => {
                packagesByProtocolType[protocolType].forEach((promotedPkg: MinimalPackageDetails) => {
                    const existingPackage = find(
                        packages,
                        (pkg: Package) => pkg.name === promotedPkg.id && pkg.versions[0].version === promotedPkg.version
                    );
                    if (existingPackage && !existingPackage.versions[0].views.some(v => v.id === promotedView.id)) {
                        existingPackage.versions[0].views.push(promotedView);
                    }
                });
            });

            const fullyQualifiedFeedId = Utils_String.format(
                "{0}@{1}",
                this._currentState.selectedFeed.id,
                payload.promotedView.id
            );
            const settingsDataService = this.pageContext.getService<SettingsDataService>("IFeedSettingsDataService");
            settingsDataService.setMruFullyQualifiedFeedId(fullyQualifiedFeedId);
            this._currentState.mruViewId = promotedView.id;
            Actions.MultiPromotePanelClosed.invoke({});
            this.emitChanged();
        } catch (error) {
            CustomerIntelligenceHelper.publishEvent(this.pageContext, CiConstants.BatchRequestFailed);
            Actions.ErrorEncountered.invoke({ message: error.message, isCritical: true });
        } finally {
            this.PerformanceService.endScenario(scenario);
        }
    };

    private _handleMultiCommandVersionDropdownClickedAction = (packageId: string): void => {
        const projectId = this._currentState.selectedFeed.project ? this._currentState.selectedFeed.project.id : null;
        this._feedsDataService
            .getPackageVersionsAsync(projectId, this._currentState.selectedFeed.id, packageId, null, null)
            .then((versions: PackageVersion[]) => {
                const updatedIndex = findIndex(this._currentState.packages, (pkg: Package) => pkg.id === packageId);
                if (updatedIndex !== -1) {
                    this._currentState.multiCommandDropdownVersions = { packageId, versions };
                    this.emitChanged();
                }
            });
    };

    private _handleNewFeed = (newFeedPayload: INewFeedCreatedPayload): void => {
        const feed = newFeedPayload.createdFeed;
        const perfScenario = this.PerformanceService.startScenario(PerfScenarios.CreateFeed);
        this._currentState.displayWelcomeMessage = false;
        this._currentState.feeds.push(feed);
        this._currentState.feeds = this._currentState.feeds.sort(this._feedSort);

        try {
            this.feedManager.renderNewFeed(feed);
        } catch (error) {
            Actions.ErrorEncountered.invoke({
                message: error.message,
                isCritical: true
            } as IError);
        } finally {
            this.emitChanged();
        }

        this.PerformanceService.endScenario(perfScenario);
    };

    private _handleFeedListUpdatedAction = (feeds: Feed[]) => {
        this._currentState.feeds = feeds;
    };

    private _handlePackageSelectedAction = (packageSummary: Package): void => {
        this._currentState.selectedPackages = [];
        this.emitChanged();
    };

    private _handleRecyleBinPackageSelectedAction = (packageSummary: Package): void => {
        this._currentState.selectedPackages = [];
        this.emitChanged();
    };

    private _handlePackagesRestoredToFeed = (payload: IPackageDeletedPayload) => {
        RestorePackages.handlePackagesRestoredToFeedAction(payload, this._currentState.packages);
    };

    private _handlePackageListUpdatedAction = (updatedPackageIds: string[] = null): void => {
        // If payload has an array of updated package ids, only update those packages.
        if (updatedPackageIds && updatedPackageIds.length > 0) {
            const updatedPackages = this._currentState.packages.map((pkg, index) => {
                if (pkg && pkg.versions && pkg.versions.length > 0 && updatedPackageIds.indexOf(pkg.id) !== -1) {
                    const projectId = this._currentState.selectedFeed.project
                        ? this._currentState.selectedFeed.project.id
                        : null;
                    return this._feedsDataService
                        .getPackageAsync(this._currentState.selectedFeed, pkg.id, /*includeDescription*/ true)
                        .then(
                            (packageResult: Package) => {
                                this._currentState.packages[index] = packageResult;
                            },
                            error => {
                                // no versions of package found/available
                                this._currentState.packages.splice(index, 1);
                            }
                        );
                }
            });
            Promise.all(updatedPackages).then(() => {
                this.emitChanged();
            });
        } else {
            CustomerIntelligenceHelper.publishEvent(this.pageContext, CiConstants.PackageListUpdated);
            const packageListUpdatedScenario = this.PerformanceService.startScenario(PerfScenarios.PackageListUpdated);

            this._updatePackageList(packageListUpdatedScenario);
        }
    };

    private _handlePackageListedStatusChangedAction = (payload: IPackageListedStatusChangedPayload): void => {
        this._currentState.packages.some(pkg => {
            if (pkg && payload.packageId === pkg.id) {
                pkg.versions[0].isDeleted = payload.isDeleted || pkg.versions[0].isDeleted;
                pkg.versions[0].isListed = payload.isListed;
                this.emitChanged();
                if (pkg.versions[0].isDeleted && this._currentState.selectedFeed.hideDeletedPackageVersions) {
                    this._handlePackageListUpdatedAction([pkg.id]);
                }
                return true;
            }
        });
    };

    private _addPromoteTelemetry(payload: IPackagePromotedPayload): IVssScenario {
        let promoteScenario: IVssScenario;
        if (payload.minimalPackageDetails.length > 1) {
            CustomerIntelligenceHelper.publishEvent(this.pageContext, CiConstants.PackagesPromoted);
            promoteScenario = this.PerformanceService.startScenario(PerfScenarios.PackagesPromoted);
        } else {
            CustomerIntelligenceHelper.publishEvent(this.pageContext, CiConstants.PackagePromoted);
            promoteScenario = this.PerformanceService.startScenario(PerfScenarios.PackagePromoted);
        }

        return promoteScenario;
    }

    private _handleFeedSettingsNavigateClicked = (): void => {
        this._initializeFeedSettingsState();
        this._reduxStore.dispatch(FeedHubActions.showFeedSettings());
        CustomerIntelligenceHelper.publishEvent(this.pageContext, CiConstants.FeedSettingsOpened);
    };

    private _handlePackageListedChangedAction = (payload: IPackageListedPayload) => {
        if (payload.selectedPackages) {
            CustomerIntelligenceHelper.publishEvent(
                this.pageContext,
                CiConstants.MultiplePackageVersionsListedUnlisted
            );
            const unlistScenario = this.PerformanceService.startScenario(
                PerfScenarios.MultiplePackageVersionsListedUnlisted
            );
            const nuGetPackageDetails: MinimalPackageDetails[] = [];

            payload.selectedPackages.forEach((pkg: Package) => {
                nuGetPackageDetails.push({
                    id: pkg.name,
                    version: pkg.versions[0].version
                });
            });

            const nuGetBatchRequest: NuGetPackagesBatchRequest = {
                data: { listed: payload.isListed },
                operation: NuGetBatchOperationType.List,
                packages: nuGetPackageDetails
            };

            const nuGetDataService = this.pageContext.getService<IFeedNuGetDataService>("IFeedNuGetDataService");
            const projectId = this._currentState.selectedFeed.project
                ? this._currentState.selectedFeed.project.id
                : null;
            nuGetDataService
                .updatePackageVersions(nuGetBatchRequest, projectId, this._currentState.selectedFeed.id)
                .then(
                    () => {
                        // Lie to the user that packages were already listed/unlisted
                        this._currentState.packages.forEach((pkg: Package) => {
                            const found = payload.selectedPackages.some(
                                (selectedPackage: Package) => selectedPackage.id === pkg.id
                            );
                            if (found) {
                                pkg.versions[0].isListed = payload.isListed;
                            }
                        });
                        this.emitChanged();
                        Actions.PackageListedChangedCompleted.invoke({}); // See ***Note*** above
                        this.PerformanceService.endScenario(unlistScenario);

                        const announcement = buildAnnouncementListedStatusChangedForPackage(
                            payload.selectedPackages,
                            payload.isListed
                        );
                        announce(announcement, true);
                    },
                    error => {
                        Actions.PackageListedChangedCompleted.invoke({}); // See ***Note*** above
                        Actions.ErrorEncountered.invoke({
                            message: error.message,
                            isCritical: true
                        } as IError);
                    }
                );
        }
    };

    private _includeDeletedPackages(): boolean {
        // Our client doesn't send null query parameters
        // getPackages endpoint uses false as default value for isDeleted if you don't send a value
        return PermissionHelper.isDeleted(this._currentState.selectedFeed) !== false;
    }

    private _navigateToRecycleBin(): void {
        this._reduxStore.dispatch(FeedHubActions.showRecycleBin());
        CustomerIntelligenceHelper.publishEvent(this.pageContext, CiConstants.RecycleBinOpened);
    }

    private _updatePackageList(scenario: IVssScenario, packageListMustUpdate: boolean = false) {
        const cachedModifiedPackages = PackageModifiedCache.packageModifiedCache;
        this._packagesReFetched = null;
        if (
            packageListMustUpdate ||
            (this._currentState.selectedFeed &&
                (!this._currentState.packages ||
                    scenario.scenarioName === PerfScenarios.PackageListUpdated ||
                    (cachedModifiedPackages && Object.keys(cachedModifiedPackages).length > 0)))
        ) {
            this._currentState.packagesLoading = true;
            const directUpstreamSourceId: string = this._currentState.selectedFeed.upstreamSource
                ? this._currentState.selectedFeed.upstreamSource.id
                : null;
            const projectId = this._currentState.selectedFeed.project
                ? this._currentState.selectedFeed.project.id
                : null;
            this._filterByEnabledProtocolDataService
                .getPackagesAsync(
                    this._currentState.selectedFeed,
                    this._currentState.pageSize, // top
                    null, // skip default 0
                    this._includeDeletedPackages(),
                    null, // isListed
                    this._filterText,
                    true // includeDescription
                )
                .then(
                    (packages: Package[]) => {
                        this.setCurrentStatePackages(packages, this._currentState.pageSize, true);
                        PackageModifiedCache.packageModifiedCache = {};

                        this._finishNextPageLoadingEvent();
                        this._currentState.packagesLoading = false;
                        this.PerformanceService.endScenario(scenario);
                        this._currentState.selectedPackages = [];
                        this._packagesReFetched = true;
                        this.emitChanged();

                        return packages;
                    },
                    err => {
                        Actions.ErrorEncountered.invoke({
                            message: err.message,
                            isCritical: true,
                            details: err
                        } as IError);
                    }
                )
                .then((packages: Package[]) => {
                    this._getPackageMetrics(packages);
                });
        } else {
            this.setCurrentStatePackages(this._currentState.packages, this._currentState.requestedPackageCount, true);
            this.PerformanceService.endScenario(scenario);
            this._currentState.selectedPackages = [];
            this._packagesReFetched = false;
        }
        this.emitChanged();
    }

    private _updateRecycleBinPackagesList(scenario: IVssScenario): void {
        this._currentState.packagesLoading = true;

        this._filterByEnabledProtocolDataService
            .getRecycleBinPackagesAsync(
                this._currentState.selectedFeed,
                this._currentState.pageSize,
                0, // skip
                null
            )
            .then(
                (packages: Package[]) => {
                    this.setRecycleBinPackages(packages);
                    this._currentState.packagesLoading = false;
                    this.PerformanceService.endScenario(scenario);
                    this._currentState.selectedPackages = [];
                    this.emitChanged();
                },
                (error: Error) => {
                    this._currentState.packagesLoading = false;
                    this.PerformanceService.endScenario(scenario);
                    Actions.ErrorEncountered.invoke({
                        message: error.message,
                        isCritical: true
                    } as IError);
                }
            );
    }

    private _feedSort(f1: Feed, f2: Feed): number {
        const feedName1 = f1.name.toLocaleLowerCase();
        const feedName2 = f2.name.toLocaleLowerCase();
        if (feedName1 > feedName2) {
            return 1;
        }

        if (feedName1 < feedName2) {
            return -1;
        }

        return 0;
    }

    private _updateSelectedViewFilter(filterState: IFilterState, viewName: string): IFilterState {
        if (this._currentState.selectedFeed == null || this._currentState.feedViews == null) {
            return filterState;
        }

        viewName = this._parseViewName(viewName);
        const index = findIndex(this._currentState.feedViews, (view: FeedView) => {
            return view.name === viewName || view.id === viewName;
        });

        if (index === -1) {
            Actions.ErrorEncountered.invoke({
                message: Utils_String.format(
                    PackageResources.Error_UrlFeedNotFound,
                    getFullyQualifiedFeedName(this._currentState.selectedFeed, viewName)
                )
            } as IError);
        } else {
            const view = this._currentState.feedViews[index];
            this._currentState.selectedFeed.view = view;
            this._reduxStore.dispatch(
                FeedHubActions.feedSelected(getFullyQualifiedFeedId(this._currentState.selectedFeed))
            );
            filterState = PackageFilterBarHelper.setFeedViewToFilterState(filterState, view);
        }

        return filterState;
    }

    private _parseViewName(viewName: string): string {
        if (!viewName) {
            return null;
        }

        return viewName.indexOf("@") !== -1 ? viewName.split("@")[1] : viewName;
    }

    private _getFullyQualifiedFeedId(): string {
        if (this._currentState.selectedFeed.view && this._currentState.selectedFeed.view.id) {
            return this._currentState.selectedFeed.id + "@" + this._currentState.selectedFeed.view.id;
        } else {
            return this._currentState.selectedFeed.id;
        }
    }

    private _handleUpstreamDisabled = (upstreamSource?: string) => {
        if (upstreamSource && upstreamSource !== PackageResources.UpstreamSourceKey_All) {
            Actions.ErrorEncountered.invoke({
                message: PackageResources.Error_UpstreamSourcesNotEnabled
            });
        } else {
            this._currentState.selectedFeed.upstreamSource = null;
        }
    };

    private _finishNextPageLoadingEvent(): void {
        if (this._currentState.packages && this._currentState.packages.length > 0) {
            this._currentState.nextPageLoading = false;
        }
    }

    private _getFilterFeedView(filterState: IFilterState): FeedView {
        let feedView: FeedView;
        const filterFeedViewState: IFilterItemState = filterState[PackageFilterBarConstants.ViewFilterKey];

        if (filterFeedViewState) {
            const dropdownValues: IPickListItem[] = filterFeedViewState.value;

            if (dropdownValues && dropdownValues.length > 0) {
                feedView = filterFeedViewState.value[0] as FeedView;
                return feedView;
            }
        }

        return feedView;
    }

    private setCurrentStatePackages(packages: Package[], requestedCount: number, applyCache: boolean = false): void {
        this._currentState.packages = packages;
        this._currentState.requestedPackageCount = requestedCount;
        if (packages == null || packages.length === 0) {
            return;
        }

        if (applyCache) {
            this._currentState.packages = PackageModifiedCache.applyCacheByPackageList(packages);
        }
    }

    private _correctLatestVersion(packageSummary?: Package) {
        if (this._currentState.selectedFeed) {
            if (
                this._packagesReFetched === false &&
                packageSummary != null &&
                this._currentState.packages &&
                (isV2Feed(this._currentState.selectedFeed) ||
                    !packageSummary.isCached ||
                    !packageSummary.versions[0].isCachedVersion)
            ) {
                // Correct the latest version if getting all versions in the versions list updated the latest version.
                // Removing all newer versions that are not in the view and/or are not listed.
                const index = findIndex(this._currentState.packages, (pkg: Package) => pkg.id === packageSummary.id);
                if (index !== -1) {
                    const versions = this._currentState.packages[index].versions;
                    if (this._currentState.selectedFeed.view) {
                        const viewFoundInLatestVersion = versions[0].views.some(
                            (view: FeedView) => view.id === this._currentState.selectedFeed.view.id
                        );
                        if (!viewFoundInLatestVersion) {
                            // must remove earlier versions that don't exist in a view (clearing filters will make a new API call and undo this)
                            versions.splice(0, 1);
                            for (let i = 0; i < versions.length; i++) {
                                const viewFoundinVersion = versions[i].views.some(
                                    (view: FeedView) => view.id === this._currentState.selectedFeed.view.id
                                );
                                if (viewFoundinVersion && !versions[i].isDeleted && versions[i].isListed) {
                                    // remove all newer versions before this index
                                    versions.splice(0, i);
                                    break;
                                }
                            }
                        }
                    } else {
                        for (let i = 0; i < versions.length; i++) {
                            if (!versions[i].isDeleted && versions[i].isListed) {
                                // remove all newer versions before this index
                                versions.splice(0, i);
                                break;
                            }
                        }
                    }
                }

                this.emitChanged();
            }
        }
    }

    private setRecycleBinPackages = (packages: Package[]): void => {
        this._currentState.recycleBinState.recycleBinPackages = packages || [];
    };

    private appendRecycleBinPackages = (packages: Package[]): void => {
        const recycleBinState = this._currentState.recycleBinState;

        recycleBinState.recycleBinPackages = [].concat(recycleBinState.recycleBinPackages, packages || []);
    };

    private get ProtocolProvider(): IFeedProtocolProvider {
        return this.pageContext.getService<IFeedProtocolProvider>("IFeedProtocolProvider");
    }

    private _currentState: FeedState;
    private _reduxStore: IModuleStore<any>;

    private _feedsDataService: IFeedDataService;
    private _recycleBinDataService: RecycleBinDataService;
    private _webPageDataService: HubWebPageDataService;
    private _filterByEnabledProtocolDataService: FilterByEnabledProtocolDataService;

    private _filterText: string;
    private _packagesReFetched: boolean;
    private feedManager: FeedManager;
}
